(function() {
  'use strict';

  class RecargoAutomatico {
    constructor() {
      this.recargoProductHandle = 'recargo-equivalencia';
      this.recargoVariantId = null;
      this.recargoPercentage = 0.052; // 5.2%
      this.recargoBase = 0.00; // 6€ base
      this.processing = false;
      this.checkoutInProgress = false;
      this.init();
    }

    async init() {
      console.log('Iniciando sistema de recargo automático...');
      await this.getRecargoVariantId();
      if (!this.recargoVariantId) {
        console.error('No se pudo obtener el variant ID del producto de recargo');
        return;
      }
      this.interceptCheckoutButtons();
      await this.checkAndApplyRecargo();
      this.setupCartListeners();
    }

    async getRecargoVariantId() {
      try {
        const response = await fetch(`/products/${this.recargoProductHandle}.js`);
        if (!response.ok) {
          throw new Error(`Producto ${this.recargoProductHandle} no encontrado`);
        }
        const product = await response.json();
        this.recargoVariantId = product.variants[0].id;
        console.log('✅ Variant ID del recargo obtenido:', this.recargoVariantId);
      } catch (error) {
        console.error('❌ Error obteniendo producto de recargo:', error);
      }
    }

    interceptCheckoutButtons() {
      const checkoutSelectors = [
        'input[name="checkout"]', 'button[name="checkout"]', '.cart__submit', '[name="checkout"]',
        '.checkout-button', 'a[href="/checkout"]'
      ];
      const observer = new MutationObserver(() => this.attachCheckoutListeners(checkoutSelectors));
      observer.observe(document.body, { childList: true, subtree: true });
      this.attachCheckoutListeners(checkoutSelectors);
    }

    attachCheckoutListeners(selectors) {
      selectors.forEach(selector => {
        document.querySelectorAll(selector + ':not([data-recargo-attached])').forEach(button => {
          button.setAttribute('data-recargo-attached', 'true');
          if (this.isCheckoutButton(button)) {
            button.addEventListener('click', (e) => this.handleCheckoutClick(e), true);
            console.log('✅ Listener añadido a botón de checkout');
          }
        });
      });
    }

    isCheckoutButton(button) {
      const indicators = ['checkout', 'finalizar', 'pagar'];
      const text = (button.textContent || button.value || '').toLowerCase();
      return indicators.some(ind => text.includes(ind)) || button.href?.includes('/checkout');
    }

    async handleCheckoutClick(e) {
      if (this.processing || this.checkoutInProgress) return;
      this.checkoutInProgress = true;
      console.log('🔄 Procesando checkout...');
      try {
        const cart = await this.getCart();
        if (!this.isRecargoEnabled(cart)) {
          console.log('ℹ️ Recargo no habilitado');
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        await this.ensureRecargoApplied(cart);
        setTimeout(() => window.location.href = '/checkout', 800);
      } catch (error) {
        console.error('❌ Error en checkout:', error);
        this.checkoutInProgress = false;
      }
    }

    setupCartListeners() {
      document.addEventListener('cart:updated', () => setTimeout(() => this.checkAndApplyRecargo(), 200));
      ['cart:change', 'cart:refresh'].forEach(event => document.addEventListener(event, () => setTimeout(() => this.checkAndApplyRecargo(), 200)));
    }

    async getCart() {
      const response = await fetch('/cart.js');
      return await response.json();
    }

    isRecargoEnabled(cart) {
      const enabled = cart.attributes?.['custom.recargo_de_equivalencia'] === 'true';
      console.log('Recargo habilitado:', enabled);
      return enabled;
    }

    hasRecargoInCart(cart) {
      return cart.items.some(item => item.properties?._recargo_auto === 'true');
    }

    calculateRecargo(cartTotal) {
      const baseAmount = cartTotal + this.recargoBase;
      const recargoAmount = baseAmount * this.recargoPercentage;
      const roundedAmount = Math.round(recargoAmount * 100) / 100;
      const quantity = Math.ceil(roundedAmount * 100);
      console.log(`💰 Cálculo: (${cartTotal.toFixed(2)} + ${this.recargoBase}) × ${this.recargoPercentage} = ${roundedAmount.toFixed(2)}€ (cantidad: ${quantity})`);
      return { amount: roundedAmount, quantity };
    }

    async checkAndApplyRecargo() {
      if (this.processing) return;
      try {
        const cart = await this.getCart();
        if (this.isRecargoEnabled(cart)) {
          if (!this.hasRecargoInCart(cart)) {
            console.log('🔄 Aplicando recargo...');
            await this.ensureRecargoApplied(cart);
          }
        } else {
          if (this.hasRecargoInCart(cart)) {
            console.log('🗑️ Removiendo recargo...');
            await this.removeRecargo(cart);
          }
        }
      } catch (error) {
        console.error('❌ Error en check:', error);
      }
    }

    async ensureRecargoApplied(cart) {
      this.processing = true;
      try {
        if (this.hasRecargoInCart(cart)) {
          await this.removeRecargo(cart);
          cart = await this.getCart();
        }
        const cartTotal = cart.items.filter(item => !item.properties?._recargo_auto).reduce((total, item) => total + (item.line_price / 100), 0);
        console.log('💰 Total sin recargo:', cartTotal);
        if (cartTotal > 0) {
          const { amount, quantity } = this.calculateRecargo(cartTotal);
          await this.addRecargoToCart(amount, cartTotal, quantity);
          console.log('✅ Recargo aplicado');
        }
      } catch (error) {
        console.error('❌ Error aplicando:', error);
      } finally {
        this.processing = false;
      }
    }

    async removeRecargo(cart) {
      const recargoItems = cart.items.filter(item => item.properties?._recargo_auto === 'true');
      for (const item of recargoItems) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.variant_id, quantity: 0 })
        });
      }
    }

    async addRecargoToCart(amount, originalTotal, quantity) {
      if (!this.recargoVariantId) return;
      const addData = {
        id: this.recargoVariantId,
        quantity,
        properties: {
          '_recargo_amount': amount.toFixed(2),
          '_recargo_auto': 'true',
          '_calculation': `(${originalTotal.toFixed(2)} + ${this.recargoBase}) × ${(this.recargoPercentage * 100)}% = ${amount.toFixed(2)}€`,
          '_timestamp': new Date().toISOString()
        }
      };
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addData)
      });
      if (!response.ok) throw new Error('Error añadiendo recargo');
      console.log('✅ Recargo añadido: ' + amount.toFixed(2) + '€');
    }
  }

  window.toggleRecargoEquivalencia = function(enabled) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { 'custom.recargo_de_equivalencia': enabled ? 'true' : 'false' }
      })
    }).then(() => {
      console.log('Recargo ' + (enabled ? 'activado' : 'desactivado'));
      window.location.reload();
    });
  };

  window.checkRecargoStatus = async function() {
    const cart = await (await fetch('/cart.js')).json();
    console.log('Estado recargo:', cart.attributes['custom.recargo_de_equivalencia']);
    return cart.attributes['custom.recargo_de_equivalencia'] === 'true';
  };

  function initRecargoAutomatico() {
    if (window.recargoAutomatico) return;
    console.log('🚀 Iniciando recargo...');
    window.recargoAutomatico = new RecargoAutomatico();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecargoAutomatico);
  } else {
    initRecargoAutomatico();
  }
})();